import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchEntityMappings, deleteEntityMapping } from "@/lib/api";
import { EntityMappingDTO, FieldPairDTO } from "@shared/api-types";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import EntityMappingFormNew from "@/components/entity-mappings/entity-mapping-form-new";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function EntityMappings() {
  const { toast } = useToast();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<EntityMappingDTO | null>(null);
  const [mappingToDelete, setMappingToDelete] = useState<EntityMappingDTO | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const { data: mappings, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['/api/entity-mappings'],
    queryFn: () => fetchEntityMappings(),
  });
  
  const deleteMutation = useMutation({
    mutationFn: (mapping: EntityMappingDTO) => 
      deleteEntityMapping(mapping.parentEntity, mapping.childEntity, mapping.parentLookupField, mapping.childLookupField),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Entity mapping deleted successfully",
      });
      refetch();
      setIsDeleteDialogOpen(false);
      setMappingToDelete(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to delete entity mapping: ${(error as Error).message}`,
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (isError) {
      toast({
        title: "Error loading entity mappings",
        description: (error as Error)?.message || "Could not load entity mappings",
        variant: "destructive",
      });
    }
  }, [isError, error, toast]);

  const handleCreateNew = () => {
    setSelectedMapping(null);
    setIsFormOpen(true);
  };

  const handleEdit = (mapping: EntityMappingDTO) => {
    setSelectedMapping(mapping);
    setIsFormOpen(true);
  };

  const handleDuplicate = (mapping: EntityMappingDTO) => {
    const duplicatedMapping: EntityMappingDTO = {
      ...mapping,
      rowKey: '' // Clear unique identifier for duplication
    };
    setSelectedMapping(duplicatedMapping);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setSelectedMapping(null);
  };

  const handleFormSubmit = () => {
    refetch();
    closeForm();
  };
  
  const handleDeleteClick = (mapping: EntityMappingDTO) => {
    setMappingToDelete(mapping);
    setIsDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = () => {
    if (mappingToDelete) {
      deleteMutation.mutate(mappingToDelete);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2">Loading entity mappings...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Entity Mappings</h1>
          <p className="text-muted-foreground">
            Configure parent-child relationships between Dataverse entities
          </p>
        </div>
        <Button onClick={handleCreateNew}>Create New Mapping</Button>
      </div>

      {isFormOpen && (
        <Dialog open={isFormOpen} onOpenChange={closeForm}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{selectedMapping ? "Edit" : "Create"} Entity Mapping</DialogTitle>
              <DialogDescription>
                Define the parent-child relationship and field mappings between entities.
              </DialogDescription>
            </DialogHeader>
            <EntityMappingFormNew
              mapping={selectedMapping || undefined}
              onSubmit={handleFormSubmit}
              onCancel={closeForm}
            />
          </DialogContent>
        </Dialog>
      )}

      {mappings && mappings.length > 0 ? (
        <div className="grid gap-6">
          {mappings.map((mapping) => (
            <Card key={mapping.rowKey}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle>
                    {mapping.parentEntity} ← {mapping.childEntity}
                  </CardTitle>
                  <div className="flex gap-2">
                  [{mapping.parentLookupField} ← {mapping.childLookupField}]
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(mapping)}>
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDuplicate(mapping)}>
                      Duplicate
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDeleteClick(mapping)}
                      className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  Lookup Field: <Badge variant="outline">{mapping.parentLookupField}</Badge>
                  Lookup Field: <Badge variant="outline">{mapping.childLookupField}</Badge>
                  <span className="ml-4">
                    Last Updated: {new Date(mapping.lastUpdated).toLocaleString()}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="fields">
                    <AccordionTrigger>
                      Field Mappings ({mapping.fieldMappings.length})
                    </AccordionTrigger>
                    <AccordionContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Parent Field</TableHead>
                            <TableHead>Child Field</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mapping.fieldMappings.map((field: FieldPairDTO, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{field.parentField}</TableCell>
                              <TableCell>{field.childField}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <p className="text-muted-foreground mb-4">No entity mappings found</p>
            <Button onClick={handleCreateNew}>Create Your First Mapping</Button>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the entity mapping between 
              {mappingToDelete && (
                <span className="font-medium"> {mappingToDelete.parentEntity}</span>
              )} and
              {mappingToDelete && (
                <span className="font-medium"> {mappingToDelete.childEntity}</span>
              )}  and
              {mappingToDelete && (
                <span className="font-medium"> {mappingToDelete.parentLookupField}</span>
              )} and
              {mappingToDelete && (
                <span className="font-medium"> {mappingToDelete.childLookupField}</span>
              )}.
              <br /><br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}